"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { toast } from "sonner";
import { useSupabase } from "@/lib/auth/context";
import { X } from "lucide-react";

/**
 * Sign-in page with Supabase Auth
 * Supports email/password and Google OAuth
 * Preserves ?intent=vendor and ?redirect_url params
 */
export default function SignInPage() {
  const _router = useRouter();
  const sp = useSearchParams();
  const supabase = useSupabase();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [resending, setResending] = useState(false);

  // Preserve intent and redirect_url
  const intent = sp.get("intent") ?? (sp.get("vendor") ? "vendor" : null);
  const redirectUrl = sp.get("redirect_url");
  const safeRedirect = redirectUrl?.startsWith("/") ? redirectUrl : null;

  const qs = new URLSearchParams();
  if (intent === "vendor") qs.set("intent", "vendor");
  if (safeRedirect) qs.set("redirect_url", safeRedirect);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  const postAuth = `/post-auth${suffix}`;
  const signUpUrl = `/sign-up${suffix}`;

  // After sign-in, go to homepage (or redirect_url if provided, or post-auth for vendors)
  const signInRedirect = intent === "vendor" ? postAuth : safeRedirect || "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Check if the error is about email not being confirmed
      if (error.message.toLowerCase().includes("email not confirmed")) {
        setShowVerifyModal(true);
      } else {
        toast.error(error.message);
      }
      setLoading(false);
      return;
    }

    // Use full page redirect to ensure server sees the session cookies
    window.location.href = signInRedirect;
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?redirect_url=${encodeURIComponent(signInRedirect)}`,
        },
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Verification email sent! Check your inbox.");
        setShowVerifyModal(false);
      }
    } catch {
      toast.error("Failed to resend verification email");
    } finally {
      setResending(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?redirect_url=${encodeURIComponent(signInRedirect)}`,
      },
    });

    if (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-[70vh] grid place-items-center p-6">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-semibold">Sign in to Starlit Passage</h1>
            <p className="text-muted-foreground mt-2">Welcome back. Enter your credentials to continue.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-black"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent text-black"
                placeholder="Enter your password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 py-2.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Google
          </button>

          <p className="text-center text-sm">
            Don't have an account?{" "}
            <Link href={signUpUrl} className="font-medium underline underline-offset-4">
              Sign up
            </Link>
          </p>

          <Link
            href="/forgot-password"
            className="block text-center text-sm text-muted-foreground underline underline-offset-4"
          >
            Forgot password?
          </Link>

          <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
            By signing in, you agree to the{" "}
            <Link href="/terms" className="underline underline-offset-4">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link href="/privacy" className="underline underline-offset-4">
              Privacy Policy
            </Link>
            , and acknowledge our{" "}
            <Link href="/cookies" className="underline underline-offset-4">
              Cookie Policy
            </Link>
            .
          </p>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Email Not Verified</h2>
              <button
                type="button"
                onClick={() => setShowVerifyModal(false)}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Your email address hasn't been verified yet. Please check your inbox for the verification link, or request
              a new one.
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                className="w-full bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {resending ? "Sending..." : "Resend Verification Email"}
              </button>
              <button
                type="button"
                onClick={() => setShowVerifyModal(false)}
                className="w-full border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
